import { UserManager } from "oidc-client-ts";

const oAuthSettings = {
  authority: process.env.REACT_APP_OAUTH_AUTHORITY,
  client_id: process.env.REACT_APP_OAUTH_CLIENT_ID,
  redirect_uri: process.env.REACT_APP_OAUTH_REDIRECT_URI,
  silent_redirect_uri: process.env.REACT_APP_OAUTH_SILENT_REDIRECT_URI,
  post_logout_redirect_uri: process.env.REACT_APP_OAUTH_POST_LOGOUT_URI,
  response_type: process.env.REACT_APP_OAUTH_RESPONSE_TYPE,
  scope: process.env.REACT_APP_OAUTH_SCOPE,
  automaticSilentRenew: true,
  revokeTokensOnSignout: true,
};

class AuthBusiness {
  readonly userManager = new UserManager(oAuthSettings);
  private readonly inactivityMs = 5 * 60 * 1000;
  private readonly lastActivityKey = "bbac.last_activity";
  private readonly reauthKey = "bbac.pending_reauth";

  constructor() {
    this.userManager.events.addUserLoaded(() => this.updateLastActivity());
  }

  public updateLastActivity() {
    window.localStorage.setItem(this.lastActivityKey, String(Date.now()));
  }

  public isInactive() {
    const raw = window.localStorage.getItem(this.lastActivityKey);
    if (!raw) {
      return true;
    }

    return Date.now() - Number(raw) >= this.inactivityMs;
  }

  public async getUser() {
    const user = await this.userManager.getUser();
    return user;
  }

  public async isAuthenticated() {
    let token = await this.getAccessToken();

    return !!token;
  }

  public async handleOAuthCallback(callbackUrl: string) {
    try {
      const user = await this.userManager.signinRedirectCallback(callbackUrl);
      return user;
    } catch (e) {
      alert(e);
      console.log(`error while handling oauth callback: ${e}`);
    }
  }

  public async sendOAuthRequest() {
    return await this.userManager.signinRedirect();
  }

  public async logoutAndReauth() {
    if (window.localStorage.getItem(this.reauthKey)) {
      window.localStorage.removeItem(this.reauthKey);
      await this.sendOAuthRequest();
      return;
    }

    window.localStorage.setItem(this.reauthKey, "1");
    await this.logout();
  }

  // renews token using refresh token
  public async renewToken() {
    const user = await this.userManager.signinSilent();

    return user;
  }

  public async getAccessToken() {
    const user = await this.getUser();
    return user?.access_token;
  }

  public async logout() {
    await this.userManager.clearStaleState();
    await this.userManager.signoutRedirect();
  }
}

export const authBusiness = new AuthBusiness();
